import { Fragment, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../core/useAuth';
import { decodeToken } from '../core/decodeToken';
import { setStorageSubject } from '../../../utils/userStorage';

/*
 * Under whose name the browser remembers things from here on down: the
 * subject uuid of the login (`sub` in the token — see
 * utils/userStorage).
 *
 * Set while rendering rather than in an effect: everything below reads
 * the store in its own first render, and by then the key has to name
 * the right user. And keyed on that user, so a different login is a
 * different set of remembered things — what read the old one is set up
 * afresh instead of keeping what it read.
 */
export function UserStorageScope({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const subject = decodeToken(token)?.claims?.sub ?? null;

  setStorageSubject(subject);

  /* the query cache is not keyed by user, so what the last login was
     shown would survive into the next one — the rbac context
     (staleTime Infinity) then keeps answering with the previous
     subject and e.g. the grant form counts the wrong held roles.

     Only a real previous login is forgotten. The step from nobody to
     the first user is not a switch but the session arriving — on a
     reload it commits in the very render that mounts the dashboard,
     and the children's effects have started their fetches before this
     effect runs (child effects run first). A clear() at that moment
     removes the in-flight queries from the cache; their answers land
     in dead queries, the observers never hear, and every page stands
     on its skeleton until something else forces a render. */
  const lastSubject = useRef(subject);
  useEffect(() => {
    if (lastSubject.current === subject) return;
    const previous = lastSubject.current;
    lastSubject.current = subject;
    if (previous !== null) queryClient.clear();
  }, [subject, queryClient]);

  return <Fragment key={subject ?? 'nobody'}>{children}</Fragment>;
}
